import { lazy } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { seoHead, seoHeadFromPage } from "@/config/seoHead";
import { buildTitle } from "@/config/seoTitles";
import { scrapedPageQueryOptions } from "@/hooks/useScrapedPage";
import { lookupSlug, notFoundUnlessRedirected } from "@/lib/slugGuards";

const ReportArticlePage = lazy(() => import("@/pages/ReportArticle"));

export const Route = createFileRoute("/rapporter/$slug")({
  loader: async ({ params, context }) => {
    // useContent drar in de statiska nyhets- och rapportarkiven – ladda dem bara här.
    const { monthlyReportQueryOptions, monthlyReportsQueryOptions, neighbours } = await import("@/hooks/useContent");
    const path = `/rapporter/${params.slug}`;
    const [report, list] = await Promise.all([
      lookupSlug(() => context.queryClient.ensureQueryData(monthlyReportQueryOptions(params.slug))),
      context.queryClient.ensureQueryData(monthlyReportsQueryOptions()).catch(() => null),
    ]);
    if (report) {
      // Bara titel och slug för föregående/nästa – hela listan behövs inte i HTML:en.
      const { newer, older } = neighbours(list, params.slug);
      const link = (r: { slug: string; title: string } | null) => (r ? { slug: r.slug, title: r.title } : null);
      return { report, page: null, newer: link(newer), older: link(older) };
    }
    const page = await lookupSlug(() => context.queryClient.ensureQueryData(scrapedPageQueryOptions(path)));
    // Finns varken i arkivet eller bland de dynamiska sidorna: riktig 404.
    // (undefined = uppslagningen misslyckades – rendera hellre än att ge en falsk 404.)
    if (report === null && page === null) await notFoundUnlessRedirected(path);
    return { report: null, page: page ?? null, newer: null, older: null };
  },
  head: ({ params, loaderData }) => {
    const path = `/rapporter/${params.slug}`;
    if (loaderData?.report) {
      return seoHead(path, {
        title: buildTitle(loaderData.report.title, "article"),
        description: loaderData.report.summary,
        ogType: "article",
      });
    }
    return seoHeadFromPage(path, loaderData?.page, { ogType: "article" });
  },
  component: ReportRoute,
});

function ReportRoute() {
  const { report, page, newer, older } = Route.useLoaderData();
  return <ReportArticlePage report={report} page={page} newer={newer} older={older} />;
}
